/**
 * Dungeon Room Blueprints
 * Defines themed room templates with entity placement rules.
 * Each blueprint specifies what entities to place and where (center/edge/corner/random).
 */

import type { GridMap, Room, DungeonTheme, RoomPurpose } from '../types';
import { getCell } from '../grid';
import { SeededRandom } from '../random';

// ============================================================================
// TYPES
// ============================================================================

type PlacementRule = 'center' | 'edge' | 'corner' | 'random';

interface EntityTemplate {
  type: 'npc' | 'monster' | 'object';
  name: string;
  placement: PlacementRule;
  chance?: number; // 0-1, default 1.0
}

export interface RoomBlueprint {
  purpose: RoomPurpose;
  entities: EntityTemplate[];
  weight: number;
  minSize?: number; // minimum room area (w*h) required
}

// ============================================================================
// BLUEPRINT CATALOG
// ============================================================================

const SHARED_BLUEPRINTS: RoomBlueprint[] = [
  { purpose: 'entrance', weight: 0, entities: [
    { type: 'object', name: 'Torch', placement: 'edge', chance: 0.8 },
  ]},
  { purpose: 'empty', weight: 3, entities: [] },
  { purpose: 'storage', weight: 2, entities: [
    { type: 'object', name: 'Crate', placement: 'edge' },
    { type: 'object', name: 'Barrel', placement: 'edge', chance: 0.7 },
    { type: 'object', name: 'Crate', placement: 'random', chance: 0.5 },
  ]},
];

const THEME_BLUEPRINTS: Record<DungeonTheme, RoomBlueprint[]> = {
  fortress: [
    { purpose: 'barracks', weight: 3, entities: [
      { type: 'object', name: 'Bed', placement: 'edge' },
      { type: 'object', name: 'Bed', placement: 'edge', chance: 0.7 },
      { type: 'object', name: 'Weapon Rack', placement: 'edge', chance: 0.6 },
      { type: 'monster', name: 'Guard', placement: 'random', chance: 0.8 },
    ]},
    { purpose: 'armory', weight: 2, entities: [
      { type: 'object', name: 'Weapon Rack', placement: 'edge' },
      { type: 'object', name: 'Weapon Rack', placement: 'edge', chance: 0.6 },
      { type: 'object', name: 'Armor Stand', placement: 'edge', chance: 0.5 },
      { type: 'monster', name: 'Guard', placement: 'random', chance: 0.5 },
    ]},
    { purpose: 'throne', weight: 1, minSize: 30, entities: [
      { type: 'object', name: 'Throne', placement: 'center' },
      { type: 'object', name: 'Banner', placement: 'edge', chance: 0.6 },
      { type: 'monster', name: 'Commander', placement: 'center', chance: 0.7 },
    ]},
    { purpose: 'prison', weight: 2, entities: [
      { type: 'object', name: 'Chains', placement: 'edge' },
      { type: 'object', name: 'Chains', placement: 'edge', chance: 0.5 },
      { type: 'npc', name: 'Prisoner', placement: 'random', chance: 0.6 },
    ]},
    { purpose: 'treasury', weight: 1, minSize: 16, entities: [
      { type: 'object', name: 'Chest', placement: 'center' },
      { type: 'object', name: 'Chest', placement: 'edge', chance: 0.4 },
      { type: 'object', name: 'Gold Pile', placement: 'random', chance: 0.3 },
    ]},
  ],

  crypt: [
    { purpose: 'crypt', weight: 4, entities: [
      { type: 'object', name: 'Sarcophagus', placement: 'center' },
      { type: 'object', name: 'Tomb', placement: 'edge', chance: 0.6 },
      { type: 'monster', name: 'Skeleton', placement: 'random', chance: 0.7 },
    ]},
    { purpose: 'shrine', weight: 3, entities: [
      { type: 'object', name: 'Altar', placement: 'center' },
      { type: 'object', name: 'Candelabra', placement: 'edge', chance: 0.7 },
      { type: 'monster', name: 'Shade', placement: 'random', chance: 0.5 },
    ]},
    { purpose: 'library', weight: 2, entities: [
      { type: 'object', name: 'Bookshelf', placement: 'edge' },
      { type: 'object', name: 'Bookshelf', placement: 'edge', chance: 0.6 },
      { type: 'object', name: 'Desk', placement: 'random', chance: 0.5 },
    ]},
    { purpose: 'treasury', weight: 1, minSize: 16, entities: [
      { type: 'object', name: 'Urn', placement: 'center' },
      { type: 'object', name: 'Chest', placement: 'edge', chance: 0.5 },
      { type: 'monster', name: 'Wraith', placement: 'random', chance: 0.4 },
    ]},
  ],

  lair: [
    { purpose: 'barracks', weight: 3, entities: [
      { type: 'object', name: 'Nest', placement: 'edge' },
      { type: 'object', name: 'Bone Pile', placement: 'random', chance: 0.7 },
      { type: 'monster', name: 'Beast', placement: 'random', chance: 0.8 },
    ]},
    { purpose: 'throne', weight: 1, minSize: 30, entities: [
      { type: 'object', name: 'Bone Throne', placement: 'center' },
      { type: 'object', name: 'Trophy', placement: 'edge', chance: 0.5 },
      { type: 'monster', name: 'Alpha Beast', placement: 'center', chance: 0.7 },
    ]},
    { purpose: 'treasury', weight: 1, minSize: 16, entities: [
      { type: 'object', name: 'Hoard', placement: 'center' },
      { type: 'object', name: 'Bone Pile', placement: 'random', chance: 0.5 },
    ]},
    { purpose: 'prison', weight: 2, entities: [
      { type: 'object', name: 'Cage', placement: 'edge' },
      { type: 'npc', name: 'Captive', placement: 'random', chance: 0.5 },
      { type: 'object', name: 'Bone Pile', placement: 'random', chance: 0.6 },
    ]},
  ],

  ruins: [
    { purpose: 'shrine', weight: 3, entities: [
      { type: 'object', name: 'Broken Altar', placement: 'center' },
      { type: 'object', name: 'Rubble', placement: 'random', chance: 0.7 },
      { type: 'object', name: 'Rubble', placement: 'edge', chance: 0.5 },
    ]},
    { purpose: 'library', weight: 2, entities: [
      { type: 'object', name: 'Collapsed Shelf', placement: 'edge' },
      { type: 'object', name: 'Scroll', placement: 'random', chance: 0.6 },
      { type: 'monster', name: 'Specter', placement: 'random', chance: 0.4 },
    ]},
    { purpose: 'laboratory', weight: 2, entities: [
      { type: 'object', name: 'Broken Table', placement: 'center' },
      { type: 'object', name: 'Alchemical Residue', placement: 'edge', chance: 0.6 },
      { type: 'object', name: 'Rubble', placement: 'random', chance: 0.5 },
    ]},
    { purpose: 'treasury', weight: 1, minSize: 16, entities: [
      { type: 'object', name: 'Chest', placement: 'center' },
      { type: 'object', name: 'Rubble', placement: 'random', chance: 0.6 },
    ]},
  ],

  sewer: [
    { purpose: 'barracks', weight: 3, entities: [
      { type: 'object', name: 'Bedroll', placement: 'edge' },
      { type: 'object', name: 'Barrel', placement: 'edge', chance: 0.6 },
      { type: 'monster', name: 'Rat Swarm', placement: 'random', chance: 0.7 },
    ]},
    { purpose: 'laboratory', weight: 2, entities: [
      { type: 'object', name: 'Workbench', placement: 'center' },
      { type: 'object', name: 'Cauldron', placement: 'edge', chance: 0.6 },
      { type: 'npc', name: 'Alchemist', placement: 'random', chance: 0.4 },
    ]},
    { purpose: 'storage', weight: 3, entities: [
      { type: 'object', name: 'Crate', placement: 'edge' },
      { type: 'object', name: 'Barrel', placement: 'edge', chance: 0.7 },
      { type: 'monster', name: 'Rat Swarm', placement: 'random', chance: 0.5 },
    ]},
    { purpose: 'shrine', weight: 1, entities: [
      { type: 'object', name: 'Makeshift Altar', placement: 'center' },
      { type: 'object', name: 'Candle', placement: 'edge', chance: 0.7 },
    ]},
  ],
};

// ============================================================================
// ZONE THEME LABELS (for display/narrative)
// ============================================================================

const ZONE_LABELS: Record<DungeonTheme, string[]> = {
  fortress: ['Outer Garrison', 'Inner Chambers', 'Command Vault'],
  crypt:    ['Burial Halls', 'Sanctum', 'Deep Tombs'],
  lair:     ['Hunting Grounds', 'Den', 'Heart of the Lair'],
  ruins:    ['Outer Ruins', 'Inner Sanctum', 'Forgotten Depths'],
  sewer:    ['Drainage Tunnels', 'Hideout', 'Deep Cistern'],
};

export function getZoneLabel(theme: DungeonTheme, depth: number): string {
  const labels = ZONE_LABELS[theme];
  return labels[Math.min(depth, labels.length - 1)];
}

// ============================================================================
// BLUEPRINT SELECTION
// ============================================================================

export function selectBlueprint(
  theme: DungeonTheme,
  roomArea: number,
  _distanceFromEntrance: number,
  rng: SeededRandom,
): RoomBlueprint {
  const pool = [
    ...SHARED_BLUEPRINTS.filter(b => b.purpose !== 'entrance'),
    ...THEME_BLUEPRINTS[theme],
  ].filter(b => !b.minSize || roomArea >= b.minSize);

  if (pool.length === 0) {
    return { purpose: 'empty', weight: 1, entities: [] };
  }

  // Weight based on distance: deeper rooms get rarer blueprints more often
  const totalWeight = pool.reduce((sum, b) => sum + b.weight, 0);
  let roll = rng.range(0, totalWeight);
  for (const bp of pool) {
    roll -= bp.weight;
    if (roll <= 0) return bp;
  }

  return pool[pool.length - 1];
}

/** Get the entrance blueprint. */
export function getEntranceBlueprint(): RoomBlueprint {
  return SHARED_BLUEPRINTS.find(b => b.purpose === 'entrance')!;
}

// ============================================================================
// BLUEPRINT APPLICATION
// ============================================================================

export function applyBlueprint(
  map: GridMap, room: Room, blueprint: RoomBlueprint, rng: SeededRandom, entityPrefix: string,
): void {
  const occupied = new Set<string>();

  // Mark entities already in the room
  for (const e of map.entities) {
    if (e.x >= room.x && e.x < room.x + room.width &&
        e.y >= room.y && e.y < room.y + room.height) {
      occupied.add(`${e.x},${e.y}`);
    }
  }

  let entityIdx = 0;
  for (const template of blueprint.entities) {
    if (template.chance !== undefined && !rng.bool(template.chance)) continue;

    const pos = findPlacementPos(map, room, template.placement, occupied, rng);
    if (!pos) continue;

    occupied.add(`${pos.x},${pos.y}`);
    map.entities.push({
      id: `${entityPrefix}_${entityIdx++}`,
      x: pos.x, y: pos.y,
      type: template.type,
      name: template.name,
    });
  }
}

// ============================================================================
// PLACEMENT HELPERS
// ============================================================================

function findPlacementPos(
  map: GridMap, room: Room, rule: PlacementRule,
  occupied: Set<string>, rng: SeededRandom,
): { x: number; y: number } | null {
  switch (rule) {
    case 'center': {
      const cx = room.x + Math.floor(room.width / 2);
      const cy = room.y + Math.floor(room.height / 2);
      if (!occupied.has(`${cx},${cy}`)) {
        const cell = getCell(map, cx, cy);
        if (cell && cell.terrain === 'floor') return { x: cx, y: cy };
      }
      // Fall through to random if center is occupied
      return findRandomFloor(map, room, occupied, rng);
    }
    case 'edge': {
      const candidates: Array<{ x: number; y: number }> = [];
      for (let y = room.y; y < room.y + room.height; y++) {
        for (let x = room.x; x < room.x + room.width; x++) {
          const isEdge = x === room.x || x === room.x + room.width - 1 ||
                         y === room.y || y === room.y + room.height - 1;
          if (!isEdge) continue;
          const cell = getCell(map, x, y);
          if (cell && cell.terrain === 'floor' && !occupied.has(`${x},${y}`)) {
            candidates.push({ x, y });
          }
        }
      }
      return candidates.length > 0 ? rng.pick(candidates) : findRandomFloor(map, room, occupied, rng);
    }
    case 'corner': {
      const corners = [
        { x: room.x, y: room.y },
        { x: room.x + room.width - 1, y: room.y },
        { x: room.x, y: room.y + room.height - 1 },
        { x: room.x + room.width - 1, y: room.y + room.height - 1 },
      ];
      const valid = corners.filter(c => {
        const cell = getCell(map, c.x, c.y);
        return cell && cell.terrain === 'floor' && !occupied.has(`${c.x},${c.y}`);
      });
      return valid.length > 0 ? rng.pick(valid) : findRandomFloor(map, room, occupied, rng);
    }
    case 'random':
    default:
      return findRandomFloor(map, room, occupied, rng);
  }
}

function findRandomFloor(
  map: GridMap, room: Room, occupied: Set<string>, rng: SeededRandom,
): { x: number; y: number } | null {
  const candidates: Array<{ x: number; y: number }> = [];
  for (let y = room.y; y < room.y + room.height; y++) {
    for (let x = room.x; x < room.x + room.width; x++) {
      const cell = getCell(map, x, y);
      if (cell && cell.terrain === 'floor' && !occupied.has(`${x},${y}`)) {
        candidates.push({ x, y });
      }
    }
  }
  return candidates.length > 0 ? rng.pick(candidates) : null;
}
